import { api } from '../api.js';
import { router } from '../router.js';
import { escapeHtml, showToast } from '../utils.js';
import { refresh as refreshEventMenu } from '../events-nav.js';

/**
 * Events a family member owns or co-administers, plus the create form.
 *
 * Times are entered and shown in the browser's local zone; the API stores and returns UTC
 * instants, so the conversion happens only here and only at the edges.
 */
export async function renderEvents() {
    const app = document.getElementById('app');
    app.innerHTML = `<div class="container"><p class="text-muted">Loading events…</p></div>`;

    let events = [];
    try {
        ({ events } = await api.listEvents());
    } catch (err) {
        app.innerHTML = `
            <div class="container">
                <div class="empty-state">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <h2>Couldn't load your events</h2>
                    <p>${escapeHtml(err.message)}</p>
                </div>
            </div>`;
        return;
    }

    app.innerHTML = `
        <div class="container">
            <div class="page-header">
                <h1>Events</h1>
                <button type="button" id="new-event-btn" class="btn btn-primary">
                    <i class="fa-solid fa-plus"></i> New event
                </button>
            </div>
            <p class="event-intro">
                An event gives one occasion its own page. Guests scan a QR code, confirm their
                email, and can add photos, video and guest-book messages from their own phone
                while it's running. Everything they add lands in your gallery.
            </p>
            <div id="event-list">${events.length ? events.map(eventCard).join('') : emptyState()}</div>
        </div>`;

    document.getElementById('new-event-btn').addEventListener('click', openCreateModal);

    // Home's empty Events panel links here with ?new=1 -- see pages/calendar.js for the
    // same arrangement.
    if (new URLSearchParams(window.location.search).get('new')) openCreateModal();
}

function emptyState() {
    return `
        <div class="empty-state">
            <i class="fa-solid fa-calendar-days"></i>
            <h2>No events yet</h2>
            <p>Create one for your next family occasion.</p>
        </div>`;
}

function eventCard(ev) {
    const joined = ev.role === 'guest';
    const starts = new Date(ev.starts_at);
    const ends = new Date(ev.ends_at);
    const state = ev.is_active
        ? '<span class="event-badge event-badge-live">Live now</span>'
        : Date.now() < starts.getTime()
            ? '<span class="event-badge event-badge-upcoming">Upcoming</span>'
            : '<span class="event-badge event-badge-finished">Finished</span>';

    return `
        <a href="${joined ? `/e/${ev.slug}` : `/events/${ev.id}`}" data-link class="event-card">
            <div class="event-card-main">
                <div class="event-card-title">
                    <h2>${escapeHtml(ev.name)}</h2>
                    ${state}
                    ${joined ? `<span class="event-badge event-badge-joined">Invited by ${escapeHtml(ev.owner_name ?? 'a family member')}</span>` : ''}
                </div>
                <p class="event-card-when">${escapeHtml(formatRange(starts, ends))}</p>
                ${ev.location ? `<p class="event-card-where"><i class="fa-solid fa-location-dot"></i> ${escapeHtml(ev.location)}</p>` : ''}
            </div>
            <div class="event-card-stats">
                <span title="Photos and video"><i class="fa-solid fa-image"></i> ${ev.media_count ?? 0}</span>
                <span title="Guest book entries"><i class="fa-solid fa-pen-nib"></i> ${ev.guestbook_count ?? 0}</span>
                <span title="Guests"><i class="fa-solid fa-user-group"></i> ${ev.guest_count ?? 0}</span>
            </div>
        </a>`;
}

export function formatRange(starts, ends) {
    const sameDay = starts.toDateString() === ends.toDateString();
    const date = starts.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    const from = starts.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const to = ends.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    if (sameDay) return `${date}, ${from} – ${to}`;
    const endDate = ends.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' });
    return `${date} ${from} – ${endDate} ${to}`;
}

/**
 * `datetime-local` gives and takes `YYYY-MM-DDTHH:mm` in *local* time with no zone, so it
 * has to be converted both ways rather than sliced off an ISO string -- slicing an ISO
 * string would silently present UTC as if it were local.
 */
export function toLocalInputValue(date) {
    const offsetMs = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function openCreateModal() {
    const now = new Date();
    const start = new Date(now.getTime() + 60 * 60_000);
    const end = new Date(now.getTime() + 6 * 60 * 60_000);

    const { close, body } = openModal('New event', `
        <form id="create-event-form" class="event-form">
            <div class="form-group">
                <label for="ev-name">Name</label>
                <input type="text" id="ev-name" maxlength="200" required placeholder="Mum &amp; Dad's 50th">
            </div>
            <div class="form-group">
                <label for="ev-location">Where <span class="text-faint">(optional)</span></label>
                <input type="text" id="ev-location" maxlength="200" placeholder="The village hall">
            </div>
            <div class="form-group">
                <label for="ev-description">Welcome message <span class="text-faint">(optional)</span></label>
                <textarea id="ev-description" rows="3" maxlength="2000"
                    placeholder="A note guests will see when they scan the code."></textarea>
            </div>
            <div class="event-form-row">
                <div class="form-group">
                    <label for="ev-starts">Starts</label>
                    <input type="datetime-local" id="ev-starts" required value="${toLocalInputValue(start)}">
                </div>
                <div class="form-group">
                    <label for="ev-ends">Ends</label>
                    <input type="datetime-local" id="ev-ends" required value="${toLocalInputValue(end)}">
                </div>
            </div>
            <p class="form-hint">
                Guests can only upload between these times, and their access ends when the event does.
            </p>
            <div class="form-group">
                <label for="ev-collection">Gallery collection <span class="text-faint">(optional)</span></label>
                <input type="text" id="ev-collection" maxlength="255" placeholder="Defaults to the event name">
                <p class="form-hint">Photos guests upload are filed here in your gallery.</p>
            </div>
            <div id="create-error" class="form-error hidden"></div>
            <div class="modal-actions">
                <button type="button" class="btn btn-secondary" id="cancel-create">Cancel</button>
                <button type="submit" class="btn btn-primary">Create event</button>
            </div>
        </form>`);

    body.querySelector('#cancel-create').addEventListener('click', close);
    body.querySelector('#create-event-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const error = body.querySelector('#create-error');
        error.classList.add('hidden');

        const starts = new Date(body.querySelector('#ev-starts').value);
        const ends = new Date(body.querySelector('#ev-ends').value);
        if (!(ends > starts)) {
            error.textContent = 'The end time must be after the start time.';
            error.classList.remove('hidden');
            return;
        }

        try {
            const created = await api.createEvent({
                name: body.querySelector('#ev-name').value,
                location: body.querySelector('#ev-location').value,
                description: body.querySelector('#ev-description').value,
                collection: body.querySelector('#ev-collection').value,
                starts_at: starts.toISOString(),
                ends_at: ends.toISOString(),
            });
            close();
            showToast('Event created', 'success');
            await refreshEventMenu();
            router.navigate(`/events/${created.id}`);
        } catch (err) {
            error.textContent = err.message;
            error.classList.remove('hidden');
        }
    });
}

/**
 * Local modal helper, matching gallery.js's rather than importing it -- that one is
 * private to its module. Escape-to-close included, which gallery.js's does not have.
 */
export function openModal(title, bodyHtml, { wide = false } = {}) {
    const overlay = document.createElement('div');
    overlay.className = `modal show${wide ? ' modal-wide' : ''}`;
    overlay.innerHTML = `
        <div class="modal-content">
            <div class="modal-header">
                <h2>${escapeHtml(title)}</h2>
                <button type="button" class="modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">${bodyHtml}</div>
        </div>`;

    const onKey = (e) => { if (e.key === 'Escape') close(); };
    function close() {
        overlay.remove();
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', onKey);
    }

    overlay.querySelector('.modal-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    document.body.classList.add('modal-open');
    return { close, body: overlay.querySelector('.modal-body'), overlay };
}
